import { FC, useCallback, useState } from 'react';
import ShortcutHint from '@components/EntityDrawers/components/ShortcutHint';
import { isKeyboardSaveHintDrawer } from '@constants/entities/drawers';
import { useProject } from '@context/Project/ProjectProvider';
import { Box } from '@mui/material';

/** Persists like a cookie: dismissed until the user clears site data / this key. Survives closing the browser. */
const HINT_DISMISSED_STORAGE_KEY = 'shortcut-edit-hint';

function readHintDismissedPersisted(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(HINT_DISMISSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Bottom-center overlay on the canvas when entity edit or relationship drawer is open. */
const CanvasEntityEditSaveHint: FC = () => {
  const { drawer, viewOnly } = useProject();

  const eligible =
    !!drawer?.type && !viewOnly && isKeyboardSaveHintDrawer(drawer.type);

  const [persistentlyDismissed, setPersistentlyDismissed] = useState(
    readHintDismissedPersisted,
  );

  const handleDismissHint = useCallback(() => {
    try {
      window.localStorage.setItem(HINT_DISMISSED_STORAGE_KEY, 'true');
    } catch {
      // Private mode / quota — still hide until next visit in a normal profile
    }
    setPersistentlyDismissed(true);
  }, []);

  if (!eligible || persistentlyDismissed) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        left: '45%',
        bottom: ({ spacing }) => spacing(6),
        transform: 'translateX(-50%)',
        zIndex: 2,
        maxWidth: { xs: 'calc(100% - 48px)', sm: 560 },
        width: 'max-content',
        pointerEvents: 'auto',
      }}
    >
      <ShortcutHint onDismiss={handleDismissHint} />
    </Box>
  );
};

export default CanvasEntityEditSaveHint;
